import {
    BaseFieldItem,
    TextField,
    NumberField,
    SelectField,
    ToggleField,
    DateField,
    AutocompleteField,
    CheckboxField,
    RangeField,
    FieldType,
} from '../models/field-models';
import { SelectableOption, SelectableValue } from '../models/selectables';
import { Action } from '@ngrx/store';
import { setFieldValue } from '../actions/form-actions';

// Common type for field props (all optional except id and name)
type FieldProps<T extends BaseFieldItem> = Omit<T, 'id' | 'name' | 'type'>;

export interface FieldActions<T> {
    setValue: (value: T) => Action;
}

export interface FieldWithActions<T> extends BaseFieldItem<T> {
    _actions: FieldActions<T>;
}

export const field = {
    /**
     * Create a text field configuration
     *
     * @example
     * field.text('nameField', 'name', {
     *   value: existingValue,
     *   placeholder: 'Enter your name',
     *   validators: [required()],
     *   panelClass: 'full-width'
     * })
     */
    text: function (
        id: string,
        name: string,
        props: FieldProps<TextField> = {},
    ): TextField & { _actions: FieldActions<string> } {
        return {
            type: FieldType.Text,
            id,
            name,
            value: props.value ?? null,
            visible: props.visible !== false,
            validators: props.validators ?? [],
            mask: props.mask,
            allowEmailCharacters: props.allowEmailCharacters,
            allowPhoneCharacters: props.allowPhoneCharacters,
            ...props,
            _actions: {
                setValue: (value: string) => setFieldValue<string>({ fieldId: id, value }),
            },
        };
    },

    /**
     * Create a number field configuration
     */
    number: (
        id: string,
        name: string,
        props: FieldProps<NumberField> = {},
    ): NumberField & { _actions: FieldActions<number> } => {
        return {
            type: FieldType.Number,
            id,
            name,
            value: props.value ?? null,
            visible: props.visible !== false,
            validators: props.validators ?? [],
            mask: props.mask,
            ...props,
            _actions: {
                setValue: (value: number) => setFieldValue<number>({ fieldId: id, value }),
            },
        };
    },

    /**
     * Create a select dropdown field configuration
     */
    select: <OptionsModel extends SelectableOption = unknown, Value extends SelectableValue = string>(
        id: string,
        name: string,
        props: FieldProps<SelectField<OptionsModel, Value>>,
    ): SelectField<OptionsModel, Value> & { _actions: FieldActions<Value> } => {
        return {
            type: FieldType.Select,
            id,
            name,
            value: props.value ?? null,
            visible: props.visible !== false,
            validators: props.validators ?? [],
            ...props,
            _actions: {
                setValue: (value: Value) => setFieldValue<Value>({ fieldId: id, value }),
            },
        };
    },

    /**
     * Create a toggle switch field configuration
     */
    toggle: (
        id: string,
        name: string,
        props: FieldProps<ToggleField> = {},
    ): ToggleField & { _actions: FieldActions<boolean> } => {
        return {
            type: FieldType.Toggle,
            id,
            name,
            value: props.value ?? false,
            visible: props.visible !== false,
            validators: props.validators ?? [],
            ...props,
            _actions: {
                setValue: (value: boolean) => setFieldValue<boolean>({ fieldId: id, value }),
            },
        };
    },

    /**
     * Create a date picker field configuration
     */
    date: (
        id: string,
        name: string,
        props: FieldProps<DateField> = {},
    ): DateField & { _actions: FieldActions<Date | string> } => {
        return {
            type: FieldType.Date,
            id,
            name,
            value: props.value ?? null,
            visible: props.visible !== false,
            datePickerType: props.datePickerType ?? 'date',
            validators: props.validators ?? [],
            ...props,
            _actions: {
                setValue: (value: Date | string) => setFieldValue<Date | string>({ fieldId: id, value }),
            },
        };
    },

    /**
     * Create an autocomplete field configuration
     */
    autocomplete: <OptionsModel extends SelectableOption = unknown, Value extends SelectableValue = string>(
        id: string,
        name: string,
        props: FieldProps<AutocompleteField<OptionsModel, Value>>,
    ): AutocompleteField<OptionsModel, Value> & { _actions: FieldActions<Value> } => {
        return {
            type: FieldType.Autocomplete,
            id,
            name,
            value: props.value ?? null,
            visible: props.visible !== false,
            clearable: props.clearable ?? false,
            autoClearOnFocus: props.autoClearOnFocus ?? false,
            validators: props.validators ?? [],
            ...props,
            _actions: {
                setValue: (value: Value) => setFieldValue<Value>({ fieldId: id, value }),
            },
        };
    },
    /**
     * Create a checkbox field configuration
     */
    checkbox: (
        id: string,
        name: string,
        props: FieldProps<CheckboxField> = {},
    ): CheckboxField & { _actions: FieldActions<boolean> } => {
        return {
            type: FieldType.Checkbox,
            id,
            name,
            value: props.value ?? false,
            visible: props.visible !== false,
            validators: props.validators ?? [],
            ...props,
            _actions: {
                setValue: (value: boolean) => setFieldValue<boolean>({ fieldId: id, value }),
            },
        };
    },

    /**
     * Create a range slider field configuration
     */
    range: (
        id: string,
        name: string,
        props: FieldProps<RangeField> = {},
    ): RangeField & { _actions: FieldActions<number> } => {
        return {
            type: FieldType.Range,
            id,
            name,
            value: props.value ?? 0,
            visible: props.visible !== false,
            thumbLabel: props.thumbLabel ?? false,
            vertical: props.vertical ?? false,
            validators: props.validators ?? [],
            ...props,
            _actions: {
                setValue: (value: number) => setFieldValue<number>({ fieldId: id, value }),
            },
        };
    },
};
